<template>
    <div id="clinic-invoices-modal">
        <modal :backdrop="false" size="lg" v-model="modal" :footer="false">
            <div slot="header">
                <button @click.prevent="closeModal" class="close" type="button">×</button>
                <h4 class="smalltitle">Invoices — {{ clinic.fullName }}</h4>
            </div>
            <div class="block-body">
                <div class="billing-summary">
                    <div class="billing-info">
                        <div class="billing-row">
                            <label class="billing-label" for="clinic-invoice-email">Invoice email</label>
                            <input id="clinic-invoice-email" type="email" v-model.trim="invoiceEmail"
                                   class="form-control billing-email" :placeholder="clinic.email || 'email@example.com'">
                        </div>
                        <div class="billing-row">
                            <span class="billing-label">Bank</span>
                            <i class="fas fa-spinner fa-spin" v-if="isLoadingBilling"></i>
                            <span v-else-if="billing.hasBank" class="bank-linked">{{ billing.bankLabel }}</span>
                            <span v-else class="bank-missing">Not linked — a payment link will be emailed on Charge</span>
                        </div>
                    </div>
                    <button type="button" class="btn btn-default btn-add" @click="openForm(null)">
                        <i class="fa fa-plus"></i> Add invoice
                    </button>
                </div>

                <table class="table invoice-list">
                    <thead>
                    <tr>
                        <th>Period</th>
                        <th>Invoice #</th>
                        <th class="text-right">Total</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Paid</th>
                        <th class="text-right">Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="isLoading">
                        <td colspan="7" class="text-center"><i class="fas fa-spinner fa-spin"></i></td>
                    </tr>
                    <tr v-if="!isLoading && invoices.length === 0">
                        <td colspan="7" class="text-center">No invoices yet</td>
                    </tr>
                    <tr v-for="invoice in invoices" :key="invoice.id" v-if="!isLoading">
                        <td>
                            {{ formatPeriod(invoice.period) }}
                            <div v-if="invoice.billingEmail" class="invoice-email" title="Invoice email">{{ invoice.billingEmail }}</div>
                        </td>
                        <td>{{ invoice.invoiceNumber || '—' }}</td>
                        <td class="text-right">{{ formatMoney(invoice.total) }}</td>
                        <td>
                            <span class="invoice-status" :class="`status-${invoice.status}`"
                                  :title="invoice.failureMessage || ''">
                                {{ INVOICE_STATUS_LABEL[invoice.status] }}
                            </span>
                            <div v-if="invoice.status === INVOICE_STATUS.FAILED && invoice.failureMessage"
                                 class="failure-message">{{ invoice.failureMessage }}</div>
                        </td>
                        <td>{{ formatTime(invoice.createdAt) }}</td>
                        <td>{{ invoice.paidAt ? formatTime(invoice.paidAt) : '—' }}</td>
                        <td class="text-right">
                            <i class="fas fa-spinner fa-spin" v-if="processingId === invoice.id"></i>
                            <dropdown v-else append-to-body menu-right>
                                <a href="javascript:void(0)" data-toggle="dropdown"><i class="fa fa-cog"></i></a>
                                <template slot="dropdown">
                                    <template v-if="invoice.status === INVOICE_STATUS.DRAFT">
                                        <li><a href="javascript:void(0)" @click.prevent="charge(invoice)">Charge</a></li>
                                        <li><a href="javascript:void(0)" @click.prevent="openForm(invoice)">Edit</a></li>
                                        <li><a href="javascript:void(0)" @click.prevent="remove(invoice)">Delete</a></li>
                                    </template>
                                    <li v-if="invoice.status === INVOICE_STATUS.FAILED">
                                        <a href="javascript:void(0)" @click.prevent="retry(invoice)">Retry charge</a>
                                    </li>
                                    <li v-if="canSendLink(invoice)">
                                        <a href="javascript:void(0)" @click.prevent="sendLink(invoice)">
                                            {{ invoice.status === INVOICE_STATUS.AWAITING_PAYMENT ? 'Resend payment link' : 'Send payment link' }}
                                        </a>
                                    </li>
                                    <li v-if="canSendLink(invoice)">
                                        <a href="javascript:void(0)" @click.prevent="voidInvoice(invoice)">Void</a>
                                    </li>
                                    <li v-if="invoice.hostedInvoiceUrl">
                                        <a :href="invoice.hostedInvoiceUrl" target="_blank" rel="noopener">View invoice</a>
                                    </li>
                                    <li v-if="invoice.invoicePdfUrl">
                                        <a :href="invoice.invoicePdfUrl" target="_blank" rel="noopener">Download PDF</a>
                                    </li>
                                </template>
                            </dropdown>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <pagination v-if="paginate.total > paginate.perPage"
                            :currentPage="paginate.page"
                            :perPage="paginate.perPage"
                            :totalPages="paginate.totalPages"
                            :total="paginate.total"
                            @updatePerPage="updatePage">
                </pagination>
            </div>
        </modal>

        <clinic-invoice-form-modal
                v-if="form.modal"
                :clinic="clinic"
                :invoice="form.invoice"
                :email="invoiceEmail"
                @updateModal="form.modal = $event"
                @saved="loadInvoices">
        </clinic-invoice-form-modal>
    </div>
</template>

<script>
import httpClient from '../../../client'
import Pagination from '../../../global/Pagination.vue'
import CommonMixin from '../../../mixins/general/CommonMixin.vue'
import ClinicInvoiceMixin from '../../../mixins/user/ClinicInvoiceMixin.vue'
import ClinicInvoiceFormModal from './ClinicInvoiceFormModal.vue'

export default {
  name: 'ClinicInvoicesModal',
  components: { Pagination, ClinicInvoiceFormModal },
  props: {
    clinic: { type: Object, required: true }
  },
  mixins: [CommonMixin, ClinicInvoiceMixin],
  data () {
    return {
      modal: false,
      isLoading: false,
      isLoadingBilling: false,
      processingId: null,
      invoices: [],
      billing: { hasBank: false, bankLabel: null, email: null },
      // email nhận link thanh toán; mặc định email clinic, admin sửa được (để test)
      invoiceEmail: '',
      paginate: { page: 1, perPage: 10, total: 0, totalPages: 1 },
      form: { modal: false, invoice: null }
    }
  },
  watch: {
    modal (value) {
      this.$emit('updateModal', value)
    }
  },
  mounted () {
    this.modal = true
    this.loadBilling()
    this.loadInvoices()
  },
  methods: {
    /** Email trong ô nhập; báo lỗi và trả null nếu trống/sai định dạng */
    recipientEmail () {
      const email = (this.invoiceEmail || '').trim()
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.alertError('Please enter a valid invoice email')
        return null
      }
      return email
    },
    canSendLink (invoice) {
      return [this.INVOICE_STATUS.AWAITING_PAYMENT, this.INVOICE_STATUS.FAILED].indexOf(invoice.status) > -1
    },
    async loadBilling () {
      this.isLoadingBilling = true
      try {
        const response = await httpClient.get(`/clinic-invoices/billing-status`, { params: { clinic_id: this.clinic.id } })
        if (response.data.s === 200) {
          this.billing = response.data.data
          if (!this.invoiceEmail) {
            this.invoiceEmail = this.billing.email || this.clinic.email || ''
          }
          this.$emit('bankChanged', this.billing.bankLabel)
        } else {
          this.alertError(response.data.msg)
        }
      } catch (e) {
        this.alertException(e)
      } finally {
        this.isLoadingBilling = false
      }
    },
    async loadInvoices () {
      this.isLoading = true
      try {
        const params = { clinic_id: this.clinic.id, page: this.paginate.page, perPage: this.paginate.perPage }
        const response = await httpClient.get(`/clinic-invoices`, { params })
        if (response.data.s === 200) {
          this.invoices = response.data.data.invoices
          this.paginate.total = response.data.data.total
          this.paginate.totalPages = Math.max(1, Math.ceil(this.paginate.total / this.paginate.perPage))
        } else {
          this.alertError(response.data.msg)
        }
      } catch (e) {
        this.alertException(e)
      } finally {
        this.isLoading = false
      }
    },
    updatePage (data) {
      this.paginate.perPage = data.perPage
      this.paginate.page = data.currentPage
      this.loadInvoices()
    },
    openForm (invoice) {
      this.form.invoice = invoice
      this.form.modal = true
    },
    /** uiv $confirm reject khi bấm Cancel -> trả về false */
    async ask (title, content) {
      try {
        await this.$confirm({ title, content, okText: 'Yes', cancelText: 'Cancel' })
        return true
      } catch (e) {
        return false
      }
    },
    /** Gọi action trên 1 invoice rồi tải lại danh sách */
    async runAction (invoice, request) {
      if (this.processingId) return
      this.processingId = invoice.id
      try {
        const response = await request()
        if (response.data.s === 200) {
          const failed = response.data.data && response.data.data.status === this.INVOICE_STATUS.FAILED
          failed ? this.alertError(response.data.msg) : this.alertSuccess(response.data.msg)
        } else {
          this.alertError(response.data.msg)
        }
      } catch (e) {
        this.alertException(e)
      } finally {
        this.processingId = null
        this.loadInvoices()
      }
    },
    async charge (invoice) {
      // đọc lại trạng thái bank ngay trước khi xác nhận
      await this.loadBilling()
      const amount = this.formatMoney(invoice.total)
      // đã có bank thì trừ tiền thẳng, không cần email
      const email = this.billing.hasBank ? null : this.recipientEmail()
      if (!this.billing.hasBank && !email) return
      const content = this.billing.hasBank
        ? `Charge ${amount} from ${this.billing.bankLabel}? ACH takes about 4 business days to settle.`
        : `This clinic has no bank account on file. A payment link for ${amount} will be emailed to ${email}. Continue?`
      if (!(await this.ask('Charge invoice', content))) return
      await this.runAction(invoice, () => httpClient.post(`/clinic-invoices/${invoice.id}/charge`, { email }))
    },
    async retry (invoice) {
      if (!(await this.ask('Retry charge', `Charge ${this.formatMoney(invoice.total)} again from the bank on file?`))) return
      await this.runAction(invoice, () => httpClient.post(`/clinic-invoices/${invoice.id}/retry`))
    },
    async sendLink (invoice) {
      const email = this.recipientEmail()
      if (!email) return
      if (!(await this.ask('Send payment link', `Email a payment link to ${email}?`))) return
      await this.runAction(invoice, () => httpClient.post(`/clinic-invoices/${invoice.id}/send-link`, { email }))
    },
    async voidInvoice (invoice) {
      if (!(await this.ask('Void invoice', `Void invoice ${invoice.invoiceNumber || ''} (${this.formatMoney(invoice.total)})? This cannot be undone.`))) return
      await this.runAction(invoice, () => httpClient.post(`/clinic-invoices/${invoice.id}/void`))
    },
    async remove (invoice) {
      if (!(await this.ask('Delete draft', `Delete the ${this.formatPeriod(invoice.period)} draft?`))) return
      await this.runAction(invoice, () => httpClient.delete(`/clinic-invoices/${invoice.id}`))
    },
    closeModal () {
      this.modal = false
    }
  }
}
</script>

<style scoped>
    .billing-summary {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 15px;
    }

    .billing-info {
        flex: 1;
        min-width: 0;
    }

    .billing-row {
        display: flex;
        align-items: center;
        min-height: 36px;
        margin-bottom: 8px;
    }

    .billing-row:last-child {
        margin-bottom: 0;
    }

    .billing-label {
        width: 120px;
        flex-shrink: 0;
        margin: 0;
        font-weight: normal;
    }

    .billing-email {
        max-width: 360px;
    }

    .billing-summary .btn-add {
        flex-shrink: 0;
    }

    .bank-linked {
        color: #2e7d32;
        font-weight: 600;
    }

    .bank-missing {
        color: #8e8e93;
    }

    .invoice-status {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 12px;
        background: #eceff1;
        color: #455a64;
    }

    .invoice-status.status-1 { background: #fff3e0; color: #e65100; }
    .invoice-status.status-2 { background: #e3f2fd; color: #1565c0; }
    .invoice-status.status-3 { background: #e8f5e9; color: #2e7d32; }
    .invoice-status.status-4 { background: #ffebee; color: #c62828; }
    .invoice-status.status-5 { background: #f5f5f5; color: #9e9e9e; text-decoration: line-through; }

    .invoice-email {
        font-size: 11px;
        color: #8e8e93;
    }

    .failure-message {
        font-size: 11px;
        color: #c62828;
        max-width: 220px;
    }
</style>
